import React from 'react';
import { clsx } from '@mentimeter/ragnar-tailwind-config';
import { LoaderIcon, CheckIcon, CrossIcon } from '@mentimeter/ragnar-visuals';
import { useTranslations } from 'use-intl';
import type { ButtonContentProps } from './ButtonContent.types';
import { buttonContentVariants } from './ButtonContent.variants';
type StateIconMap = Record<
  NonNullable<ButtonContentProps['state']>,
  JSX.Element
>;

export const ButtonContent = ({
  state,
  size = 'default',
  children,
  iconLeading,
  iconTrailing,
}: ButtonContentProps) => {
  const t = useTranslations('ragnar.button');
  const IconLeading = iconLeading;
  const IconTrailing = iconTrailing;
  const iconSize = size === 'compact' ? 1 : 2;
  const buttonContentClasses = buttonContentVariants({ state: Boolean(state) });

  const stateIconMap: StateIconMap = {
    loading: (
      <LoaderIcon
        size={iconSize}
        aria-label={t('button_loading_icon_a11y')}
        color="currentColor"
      />
    ),
    success: (
      <CheckIcon
        size={iconSize}
        aria-label={t('button_success_icon_a11y')}
        color="currentColor"
      />
    ),
    error: (
      <CrossIcon
        size={iconSize}
        aria-label={t('button_error_icon_a11y')}
        color="currentColor"
      />
    ),
  };

  return (
    <>
      <span
        className={clsx([
          !state ? 'opacity-0' : 'opacity-100',
          'absolute',
          'top-0',
          'bottom-0',
          'left-0',
          'right-0',
          'flex',
          'items-center',
          'justify-center',
          'transition-opacity',
        ])}
      >
        {state ? stateIconMap[state] : null}
      </span>
      {IconLeading && (
        <span
          data-testid="leading-icon-container"
          aria-hidden
          className={clsx(buttonContentClasses, 'mr-1 ')}
        >
          <IconLeading color="inherit" size={iconSize} />
        </span>
      )}
      <span
        data-testid="button-children"
        aria-hidden={Boolean(state)}
        className={clsx(buttonContentClasses)}
      >
        {children}
      </span>

      {IconTrailing && (
        <span
          data-testid="trailing-icon-container"
          aria-hidden
          className={clsx(buttonContentClasses, 'ml-1 ')}
        >
          <IconTrailing color="currentColor" size={iconSize} />
        </span>
      )}
    </>
  );
};
